/**
 * LocalStorage 缓存工具函数
 * 用于管理带会话标识的本地存储
 */

/**
 * 会话标识机制说明：
 * - 使用 sessionStorage 存储会话ID (app_session_id)
 * - 每次页面刷新时会生成新的会话ID
 * - 通过比较缓存中的会话ID与当前会话ID来验证缓存有效性
 * - 会话ID在首次需要时自动初始化
 */

interface CachedData<T> {
  data: T;
  sessionId: string;
  timestamp: number;
}

/**
 * 存储数据到 LocalStorage，并记录会话标识
 * @param key 存储键名
 * @param data 要存储的数据
 */
export function setStorageWithSession<T>(key: string, data: T): void {
  try {
    const sessionId = sessionStorage.getItem("app_session_id") || "";
    const cachedData: CachedData<T> = {
      data,
      sessionId,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(cachedData));
  } catch (error) {
    console.error(`存储数据失败 [${key}]:`, error);
  }
}

/**
 * 从 LocalStorage 获取数据，并验证会话
 * @param key 存储键名
 * @returns 如果会话有效返回数据，否则返回 null
 */
export function getStorageWithSession<T>(key: string): T | null {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) {
      return null;
    }

    const cachedData: CachedData<T> = JSON.parse(cached);
    const currentSessionId = sessionStorage.getItem("app_session_id") || "";

    // 验证会话标识
    if (cachedData.sessionId !== currentSessionId) {
      console.log(`会话已变更，缓存失效 [${key}]`);
      return null;
    }

    return cachedData.data;
  } catch (error) {
    console.error(`读取缓存失败 [${key}]:`, error);
    return null;
  }
}

/**
 * 清除指定的缓存
 * @param key 存储键名
 */
export function clearStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`清除缓存失败 [${key}]:`, error);
  }
}

/**
 * 获取当前会话ID，如果不存在则自动创建
 */
export function getCurrentSessionId(): string {
  let sessionId = sessionStorage.getItem("app_session_id");
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    sessionStorage.setItem("app_session_id", sessionId);
    console.log("会话ID已初始化:", sessionId);
  }
  return sessionId;
}
